import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function GoogleCalendarCallback() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    // Parse the hash fragment from the URL (Google returns token in hash)
    const hash = window.location.hash.substring(1)
    const params = new URLSearchParams(hash)
    const accessToken = params.get('access_token')
    const stateToken = params.get('state')
    const error = params.get('error')

    console.log('[GoogleCallback] Processing OAuth callback', {
      hasAccessToken: !!accessToken,
      hasStateToken: !!stateToken,
      hasError: !!error
    })

    // Handle errors
    if (error || !accessToken) {
      const errorMsg = error || 'No access token received'
      console.error('[GoogleCallback] OAuth error:', errorMsg)
      setStatus('error')
      setErrorMessage(errorMsg)
      
      // Still try to notify parent
      sendResultToParent({ error: errorMsg }, stateToken)
      
      // Start countdown to redirect
      startCountdownAndRedirect()
      return
    }

    // Get user info from Google
    const fetchUserInfo = async () => {
      try {
        console.log('[GoogleCallback] Fetching user info from Google')
        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })
        
        if (!response.ok) {
          throw new Error(`Failed to fetch user info: ${response.status}`)
        }
        
        const data = await response.json()
        console.log('[GoogleCallback] Got user email:', data.email)
        
        const result = {
          access_token: accessToken,
          email: data.email
        }
        
        // Send to parent window via multiple methods for reliability
        sendResultToParent(result, stateToken)
        
        setStatus('success')
        
        // Try to close the window
        tryCloseWindow()
        
      } catch (error) {
        console.error('[GoogleCallback] Failed to fetch user info:', error)
        setStatus('error')
        setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch user info')
        
        sendResultToParent({ error: 'Failed to fetch user info' }, stateToken)
        startCountdownAndRedirect()
      }
    }

    void fetchUserInfo()
  }, [])

  // Send result to parent window via localStorage and postMessage
  const sendResultToParent = (result: { access_token?: string; email?: string; error?: string }, stateToken: string | null) => {
    console.log('[GoogleCallback] Sending result to parent', { stateToken, hasError: !!result.error })
    
    // Method 1: Save to localStorage (primary method - context polls this)
    if (stateToken) {
      try {
        localStorage.setItem(`google_oauth_result_${stateToken}`, JSON.stringify(result))
        console.log('[GoogleCallback] Saved result to localStorage with key:', `google_oauth_result_${stateToken}`)
      } catch (e) {
        console.error('[GoogleCallback] Failed to save to localStorage:', e)
      }
    }
    
    // Method 2: postMessage to opener (backup method)
    if (window.opener) {
      try {
        // Send with the correct message type that context expects
        window.opener.postMessage({
          type: 'GOOGLE_OAUTH_SUCCESS',
          state: stateToken,
          result: result
        }, window.location.origin)
        console.log('[GoogleCallback] Sent postMessage to opener')
      } catch (e) {
        console.error('[GoogleCallback] Failed to postMessage to opener:', e)
      }
    } else {
      console.log('[GoogleCallback] No window.opener available')
    }
  }

  const tryCloseWindow = () => {
    console.log('[GoogleCallback] Attempting to close window')
    
    // Try to close the window
    try {
      window.close()
    } catch (e) {
      console.log('[GoogleCallback] window.close() failed:', e)
    }
    
    // Check if window actually closed after a short delay
    setTimeout(() => {
      // If we're still here, the window didn't close
      console.log('[GoogleCallback] Window did not close, starting countdown')
      startCountdownAndRedirect()
    }, 500)
  }

  const startCountdownAndRedirect = () => {
    // Start countdown
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          // Redirect to settings page
          navigate('/booking-system/settings', { replace: true })
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleManualClose = () => {
    navigate('/booking-system/settings', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md px-4">
        {status === 'processing' && (
          <>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Connecting to Google Calendar</h1>
            <p className="text-muted-foreground">Please wait while we complete the connection...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold mb-2 text-green-600">Connected Successfully!</h1>
            <p className="text-muted-foreground mb-4">
              Your Google Calendar has been connected. This window should close automatically.
            </p>
            {countdown > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting in {countdown} seconds...
              </p>
            )}
            <button
              onClick={handleManualClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Return to Settings
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold mb-2 text-red-600">Connection Failed</h1>
            <p className="text-muted-foreground mb-4">
              {errorMessage || 'Failed to connect to Google Calendar. Please try again.'}
            </p>
            {countdown > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting in {countdown} seconds...
              </p>
            )}
            <button
              onClick={handleManualClose}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Return to Settings
            </button>
          </>
        )}
      </div>
    </div>
  )
}
